import React, { useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material";

const FIELDS = gql`
  fragment RetentionPreviewFields on GroupRetentionPreview {
    groupId title count sharedCount fingerprint lastRetentionDay scheduledAt scheduledCount completedCount failedCount enabled
    users { id name email shared protectedReason }
  }
`;
export const PREVIEW_RETENTION = gql`
  query GroupRetentionPreview($id: ID!) {
    groupRetentionPreview(id: $id) { ...RetentionPreviewFields }
  }
  ${FIELDS}
`;
export const END_GROUP_CONTRACT = gql`
  mutation EndGroupContract($id: ID!, $fingerprint: String!, $confirmation: String!) {
    endGroupContract(id: $id, fingerprint: $fingerprint, confirmation: $confirmation) { ...RetentionPreviewFields }
  }
  ${FIELDS}
`;

export default function GroupRetention({ id }: { id: string }) {
  const [opened, setOpened] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const { data, loading, error, refetch } = useQuery(PREVIEW_RETENTION, {
    variables: { id }, skip: !opened, fetchPolicy: "network-only",
  });
  const [schedule, mutation] = useMutation(END_GROUP_CONTRACT);
  const preview = mutation.data?.endGroupContract || data?.groupRetentionPreview;
  const submit = async () => {
    try {
      await schedule({ variables: { id, fingerprint: preview.fingerprint, confirmation } });
      setConfirmation(""); setAcknowledged(false);
    } catch { /* The mutation error is displayed below; don't imply success. */ }
  };
  if (!opened) return <Button color="error" variant="outlined" onClick={() => setOpened(true)}>
    Avsluta avtal och gallra användare
  </Button>;
  return <Stack spacing={2}>
    <Typography variant="h6">Avsluta avtal och gallra användare</Typography>
    <Alert severity="warning">
      Åtgärden avslutar avtalet för denna grupp och planerar gallring av de konton som är kopplade
      till gruppen när du bekräftar. Bekräftelsedagen räknas som dag 1. Kontona bevaras till och med dag 30 och gallras kl. 06.00 dag 31, svensk tid.
      Konton som tillhör andra grupper undantas från avtalsgallringen. Medlemskap kontrolleras igen före gallring.
      Diplom, intyg och annat innehåll behålls. Detta är inte radering av allt innehåll enligt avtalet.
    </Alert>
    {loading && <Alert severity="info">Hämtar berörda användare…</Alert>}
    {(error || mutation.error) && <Alert severity="error">{(error || mutation.error)?.message}</Alert>}
    {preview && <>
      <Typography>{preview.count} konton berörs. {preview.sharedCount} tillhör även andra grupper.</Typography>
      <Typography variant="h6">Konton som kommer att gallras</Typography>
      <BoxList users={preview.users.filter((user: { shared: boolean; protectedReason?: string }) => !user.shared && !user.protectedReason)} />
      <Typography variant="h6">Konton som inte kommer att gallras då de ingår i andra grupper</Typography>
      <BoxList users={preview.users.filter((user: { shared: boolean }) => user.shared)} />
      <Typography variant="h6">Konton som är undantagna från automatisk gallring</Typography>
      <BoxList users={preview.users.filter((user: { shared: boolean; protectedReason?: string }) => !user.shared && user.protectedReason)} />
      {preview.scheduledAt ? <Alert severity="success">
        Sista dagen före gallring: {preview.lastRetentionDay}. Gallring är planerad till {new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Stockholm", dateStyle: "short", timeStyle: "short" }).format(new Date(preview.scheduledAt))}, svensk tid. {preview.completedCount} av {preview.scheduledCount} konton är gallrade. {preview.failedCount} har fel som behöver följas upp.
        <Button onClick={() => { mutation.reset(); refetch(); }}>Uppdatera körstatus</Button>
      </Alert> : <>
        {!preview.enabled && <Alert severity="info">Gallring är ännu inte aktiverad i denna miljö.</Alert>}
        <TextField label={`Skriv gruppens namn: ${preview.title}`} value={confirmation}
          onChange={e => setConfirmation(e.target.value)} disabled={mutation.loading} />
        <FormControlLabel control={<Checkbox checked={acknowledged} disabled={mutation.loading}
          onChange={e => setAcknowledged(e.target.checked)} />}
          label="Jag bekräftar avtalsavslutet och att kontona under ”Konton som kommer att gallras” får gallras i samtliga tjänster." />
        <Stack direction="row" spacing={2}>
          <Button color="error" variant="contained" onClick={submit}
            disabled={loading || mutation.loading || !preview.enabled || !acknowledged || confirmation !== preview.title}>
            {mutation.loading ? "Planerar…" : "Bekräfta avtalsavslut och gallring"}
          </Button>
          <Button disabled={mutation.loading} onClick={() => { setConfirmation(""); setAcknowledged(false); refetch(); }}>Uppdatera förhandsvisning</Button>
        </Stack>
      </>}
    </>}
  </Stack>;
}

function BoxList({ users }: { users: { id: string; name: string; email: string }[] }) {
  if (!users.length) return <Typography>Inga konton.</Typography>;
  return <ul style={{ maxHeight: 240, overflow: "auto", margin: 0 }}>
    {users.map(user => <li key={user.id}>{user.name || "Namnlöst konto"} — {user.email} (ID {user.id})</li>)}
  </ul>;
}
